import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Mail, MailX, Info } from 'lucide-react';
import { api } from '../services/api';
import { assessmentService } from '../services/assessmentService';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface PlayerRow {
  id: string;
  firstName: string;
  lastName: string;
  position?: string;
  teamId: string;
  teamName: string;
  teamAgeGroup?: string;
  hasCompleted: boolean;
  hasDraft: boolean;
  hasEmail: boolean;
}

export function AssessPlayersPage() {
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoSend, setAutoSend] = useState(() => localStorage.getItem('gamepath_auto_report') !== 'false');

  useEffect(() => {
    (async () => {
      try {
        const teamsRes = await api.get('/teams');
        const teams = teamsRes.data.data || [];
        const rows: PlayerRow[] = [];
        for (const team of teams) {
          try {
            const pRes = await api.get(`/teams/${team.id}/players`);
            const teamPlayers = pRes.data.data || [];
            for (const p of teamPlayers) {
              let hasCompleted = false;
              let hasDraft = false;
              try {
                const assessments = await assessmentService.getPlayerAssessments(p.id);
                hasCompleted = assessments.some((a: any) => a.isComplete);
                hasDraft = assessments.some((a: any) => !a.isComplete);
              } catch { /* no assessments */ }
              const hasEmail = !!(p.contactEmail || (p.guardians && p.guardians.length > 0 && p.guardians.some((g: any) => g.email)));
              rows.push({
                id: p.id, firstName: p.firstName, lastName: p.lastName,
                position: p.position, teamId: team.id, teamName: team.name,
                teamAgeGroup: team.ageGroup, hasCompleted, hasDraft, hasEmail,
              });
            }
          } catch { /* skip team */ }
        }
        setPlayers(rows);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, []);

  const toggleAutoSend = () => {
    const next = !autoSend;
    setAutoSend(next);
    localStorage.setItem('gamepath_auto_report', String(next));
  };

  const statusBadge = (p: PlayerRow) => {
    if (p.hasDraft) return <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded-full">Draft</span>;
    if (p.hasCompleted) return <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-0.5 rounded-full">Completed</span>;
    return <span className="text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded-full">Not Started</span>;
  };

  return (
    <div className="p-4 lg:p-8 max-w-3xl mx-auto">
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Assess Players</h1>
        <p className="text-sm text-gray-500">Select a player to begin or continue their assessment</p>
      </div>

      {/* Automatic Report Sending */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-gray-900 text-sm">Automatic Report Sending</p>
              <p className="text-xs text-gray-500 mt-1">
                Assessment reports are <span className="font-semibold">automatically emailed</span> to players (18+) or parents/guardians (under 18) after each assessment is completed. You can change this to manual sending if you prefer to review reports before sharing.
              </p>
            </div>
          </div>
          <button onClick={toggleAutoSend} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${autoSend ? 'bg-green-600' : 'bg-gray-300'}`}
            role="switch" aria-checked={autoSend}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoSend ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>

        {/* Why share reports */}
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-green-800 leading-relaxed">
              <span className="font-semibold">Why share reports?</span> When players receive their assessment reports, they gain valuable self-awareness about their strengths and areas for improvement. This transparency fosters <span className="font-semibold">ownership of their development journey</span>, increases motivation, and enables them to track their progress over time. Players who understand their development pathway show significantly higher engagement and cumulative improvement across all four pillars (Technical, Physical, Psychological, and Social).
            </p>
          </div>
        </div>
      </div>

      {/* Player List */}
      {loading ? (
        <LoadingSpinner />
      ) : players.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">No players yet</h2>
          <p className="text-sm text-gray-500 mb-4">Add players to your team first, then come back to assess them.</p>
          <Link to="/players" className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors min-h-[44px]">
            Go to Players
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {players.map((p) => (
            <Link key={p.id} to={`/assess/${p.id}`}
              className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-5 py-4 hover:border-green-300 hover:shadow-sm transition-all min-h-[64px]">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">{p.firstName} {p.lastName}</p>
                <p className="text-xs text-gray-500">{p.teamName}{p.teamAgeGroup ? ` ${p.teamAgeGroup}` : ''} · {p.position || 'No position'}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                {!p.hasEmail && (
                  <span className="inline-flex items-center gap-1 text-xs text-red-500">
                    <MailX className="w-3.5 h-3.5" /> No email
                  </span>
                )}
                {statusBadge(p)}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
